import { inject, injectable } from "inversify";
import { GuidSelectModel, ICPLUsersClient } from "./api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { ExternalPasswordChangeModel, ICPLAuthUsersClient, NewUserModel, UserModel } from "./api/cplauthapi/CPLAuthAPI";

export interface IUsersService {
  getSelectModels(): Promise<GuidSelectModel[]>;
  setRPMUserID(rpmUserID: string): Promise<void>;
  setUseConnectLogin(useConnectLogin: boolean, userID: string): Promise<void>;
  getUserProfile(userId: string): Promise<UserModel>;
  updateUserProfile(userModel: UserModel): Promise<void>;
  getAllUsers(includeInactive?: boolean): Promise<UserModel[]>;
  createNewUser(userModel: NewUserModel): Promise<void>;
  createCoreUser(userModel: NewUserModel): Promise<string>;
  changePassword(userID: string, newPassword: string): Promise<void>;
  verifyEmail(userID: string, verificationCode: string): Promise<void>;
  changeProfilePassword(newPassword: string): Promise<void>;
  getProfile(): Promise<UserModel>;
  resendVerificationEmail(userID: string): Promise<void>;
  updateUserActiveStatus(userID: string): Promise<void>;
  passwordReset(email: string): Promise<void>;
  externalPasswordChange(passwordModel: ExternalPasswordChangeModel): Promise<void>;
  getPropertyManagerSelectModels(): Promise<GuidSelectModel[]>;
}

@injectable()
export class UsersService implements IUsersService {
  @inject(SERVICE_TYPES.CPLUsersClient) cplUsersClient!: ICPLUsersClient;
  @inject(SERVICE_TYPES.CPLAuthUsersClient) cplAuthUsersClient!: ICPLAuthUsersClient;

  async getSelectModels(): Promise<GuidSelectModel[]> {
    return this.cplUsersClient.getSelectModels();
  }

  async setRPMUserID(rpmUserID: string): Promise<void> {
    return this.cplAuthUsersClient.setRPMUserID(rpmUserID);
  }

  async setUseConnectLogin(useConnectLogin: boolean, userID: string): Promise<void> {
    return this.cplUsersClient.setUseConnectLogin(useConnectLogin, userID);
  }

  getUserProfile(userID: string): Promise<UserModel> {
    return this.cplAuthUsersClient.getUser(userID);
  }

  updateUserProfile(userModel: UserModel): Promise<void> {
    return this.cplAuthUsersClient.updateUser(userModel);
  }

  getAllUsers(includeInactive?: boolean): Promise<UserModel[]> {
    return this.cplAuthUsersClient.getAllUsers(includeInactive);
  }

  changePassword(userID: string, newPassword: string): Promise<void> {
    return this.cplAuthUsersClient.changeUserPassword(userID, newPassword);
  }

  createNewUser(userModel: NewUserModel): Promise<void> {
    return this.cplAuthUsersClient.createNewUser(userModel);
  }

  createCoreUser(userModel: NewUserModel): Promise<string> {
    return this.cplUsersClient.createCoreUser(userModel);
  }

  verifyEmail(userID: string, verificationCode: string): Promise<void> {
    return this.cplAuthUsersClient.verifyEmail(userID, verificationCode);
  }

  changeProfilePassword(newPassword: string): Promise<void> {
    return this.cplAuthUsersClient.changeProfilePassword(newPassword);
  }

  getProfile(): Promise<UserModel> {
    return this.cplAuthUsersClient.getProfile();
  }

  resendVerificationEmail(userID: string): Promise<void> {
    return this.cplAuthUsersClient.resendVerificationCode(userID);
  }

  updateUserActiveStatus(userID: string): Promise<void> {
    return this.cplAuthUsersClient.updateUserActiveStatus(userID);
  }

  passwordReset(emailAddress: string): Promise<void> {
    return this.cplAuthUsersClient.passwordReset(emailAddress);
  }

  externalPasswordChange(passwordModel: ExternalPasswordChangeModel): Promise<void> {
    return this.cplAuthUsersClient.externalPasswordChange(passwordModel);
  }

  getPropertyManagerSelectModels(): Promise<GuidSelectModel[]> {
    return this.cplUsersClient.getPropertyManagerSelectModels();
  }
}
